import { Component } from '@theme/component';

const ERROR_MESSAGE = 'Não foi possível calcular o frete. Tente novamente mais tarde.';
const FIXED_HOLIDAYS = ['01-01', '04-21', '05-01', '09-07', '10-12', '11-02', '11-15', '11-20', '12-25'];

/**
 * Calculadora de frete por CEP na página de produto.
 *
 * A Shopify só calcula frete a partir do carrinho. Por isso o produto é adicionado
 * (1 unidade), as taxas são consultadas e em seguida só essa unidade é retirada,
 * preservando o que o cliente já tinha no carrinho.
 *
 * @typedef {object} ShippingCalculatorRefs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} zip
 * @property {HTMLButtonElement} submit
 * @property {HTMLElement} status
 * @property {HTMLTableElement} results
 * @property {HTMLTableSectionElement} resultsBody
 *
 * @extends {Component<ShippingCalculatorRefs>}
 */
class ShippingCalculatorComponent extends Component {
  requiredRefs = ['form', 'zip', 'submit', 'status', 'results', 'resultsBody'];

  #busy = false;

  /**
   * Máscara 00000-000 enquanto digita.
   */
  maskZip() {
    const { zip } = this.refs;
    const digits = zip.value.replace(/\D/g, '').slice(0, 8);
    zip.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  }

  /**
   * @param {SubmitEvent} event
   */
  async calculate(event) {
    event.preventDefault();
    if (this.#busy) return;

    const zip = this.refs.zip.value.replace(/\D/g, '');
    this.#clearResults();

    if (zip.length !== 8) {
      this.#showStatus('Insira um CEP válido.', true);
      this.refs.zip.focus();
      return;
    }

    this.#setBusy(true);
    this.#showStatus('Calculando frete…');

    try {
      const state = await this.#getState(zip);
      if (!state) {
        this.#showStatus('CEP não encontrado. Confira e tente de novo.', true);
        return;
      }

      const rates = await this.#withProductInCart(() => this.#getRates(state, zip));
      this.#showStatus('');
      this.#renderRates(rates);
    } catch (error) {
      console.error(error);
      this.#showStatus(ERROR_MESSAGE, true);
    } finally {
      this.#setBusy(false);
    }
  }

  get #root() {
    return window.Shopify?.routes?.root ?? '/';
  }

  /**
   * Variante selecionada no formulário de compra; cai para a variante do bloco.
   */
  get #variantId() {
    const container = this.closest('.shopify-section') ?? document;
    const input = container.querySelector('form[action*="/cart/add"] [name="id"]');
    const value = input instanceof HTMLInputElement || input instanceof HTMLSelectElement ? input.value : '';
    return Number(value || this.dataset.variantId);
  }

  /**
   * @param {string} zip
   * @returns {Promise<string | null>} UF do CEP
   */
  async #getState(zip) {
    const response = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
    if (!response.ok) return null;
    const data = await response.json();
    return data?.erro ? null : data?.uf ?? null;
  }

  /**
   * Adiciona 1 unidade do produto, executa `task` e retira só essa unidade.
   * @template T
   * @param {() => Promise<T>} task
   * @returns {Promise<T>}
   */
  async #withProductInCart(task) {
    const response = await fetch(`${this.#root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: this.#variantId, quantity: 1 }] }),
    });
    if (!response.ok) throw new Error(`cart/add ${response.status}`);

    const added = await response.json();
    const line = added.items?.[0];

    try {
      return await task();
    } finally {
      if (line?.key) {
        await fetch(`${this.#root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: Math.max(0, line.quantity - 1) }),
        });
      }
    }
  }

  /**
   * @param {string} state
   * @param {string} zip
   */
  async #getRates(state, zip) {
    const params = new URLSearchParams({
      'shipping_address[country]': 'Brazil',
      'shipping_address[province]': state,
      'shipping_address[zip]': zip,
    });
    const response = await fetch(`${this.#root}cart/shipping_rates.json?${params}`);
    const data = await response.json();
    if (!response.ok && !data?.shipping_rates) throw new Error(JSON.stringify(data));
    return Array.isArray(data?.shipping_rates) ? data.shipping_rates : [];
  }

  /**
   * @param {Array<{ name?: string, presentment_name?: string, price: string, delivery_date?: string | null }>} rates
   */
  #renderRates(rates) {
    const { results, resultsBody } = this.refs;
    const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

    if (rates.length === 0) {
      this.#showStatus('Frete não disponível para este CEP.', true);
      return;
    }

    for (const rate of rates) {
      const row = document.createElement('tr');
      const price = Number(rate.price);
      const cells = [
        rate.presentment_name || rate.name || '',
        price === 0 ? 'Grátis' : currency.format(price),
        this.#formatDeadline(rate.delivery_date),
      ];
      for (const text of cells) {
        const cell = document.createElement('td');
        cell.textContent = text;
        row.append(cell);
      }
      resultsBody.append(row);
    }

    results.hidden = false;
  }

  /**
   * Prazo em dias úteis até a data de entrega (sem fins de semana e feriados nacionais).
   * @param {string | null | undefined} deliveryDate - AAAA-MM-DD
   */
  #formatDeadline(deliveryDate) {
    if (!deliveryDate) return 'Consultar prazo';

    const [year, month, day] = deliveryDate.split('-').map(Number);
    const end = new Date(year, month - 1, day);
    const date = new Date();
    date.setHours(0, 0, 0, 0);

    let days = 0;
    while (date < end) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6 && !isHoliday(date)) days++;
    }

    days = Math.max(days, 1);
    return days === 1 ? '1 dia útil' : `${days} dias úteis`;
  }

  #clearResults() {
    this.refs.resultsBody.replaceChildren();
    this.refs.results.hidden = true;
  }

  /**
   * @param {string} message
   * @param {boolean} [isError]
   */
  #showStatus(message, isError = false) {
    const { status } = this.refs;
    status.textContent = message;
    status.classList.toggle('shipping-calculator__status--error', isError);
  }

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.#busy = busy;
    this.refs.submit.setAttribute('aria-busy', String(busy));
  }
}

/**
 * @param {Date} date
 */
function isHoliday(date) {
  const monthDay = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  if (FIXED_HOLIDAYS.includes(monthDay)) return true;

  const goodFriday = easterSunday(date.getFullYear());
  goodFriday.setDate(goodFriday.getDate() - 2);
  return date.toDateString() === goodFriday.toDateString();
}

/**
 * @param {number} year
 */
function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

if (!customElements.get('shipping-calculator-component')) {
  customElements.define('shipping-calculator-component', ShippingCalculatorComponent);
}
